import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Stack,
  Tooltip,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import CircleIcon from '@mui/icons-material/Circle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import axios from 'axios';

const BASE_URL = `${process.env.REACT_APP_API_URL}/api/dpt/branch`;

// Admin usernames list
const ADMIN_USERNAMES = ['admin', 'admin1', 'admin2', 'admin8', 'admin9'];

// Password validation function
const validateComplexPassword = (password) => {
  const errors = [];
  if (password.length < 8) errors.push("at least 8 characters");
  if (!/[A-Z]/.test(password)) errors.push("an uppercase letter");
  if (!/[a-z]/.test(password)) errors.push("a lowercase letter");
  if (!/[0-9]/.test(password)) errors.push("a number");
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push("a special character");

  return {
    valid: errors.length === 0,
    msg: errors.join(", ")
  };
};

// Password strength calculator
const getPasswordStrength = (password) => {
  if (!password) return 0;
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
  return strength;
};

const getStrengthColor = (strength) => {
  if (strength <= 2) return "#f44336";
  if (strength <= 3) return "#ff9800";
  if (strength <= 4) return "#2196f3";
  return "#4caf50";
};

const getStrengthText = (strength) => {
  if (strength <= 2) return "Weak";
  if (strength <= 3) return "Fair";
  if (strength <= 4) return "Good";
  return "Strong";
};

const yellowButton = {
  background: "#FFD600",
  color: "#000000",
  fontWeight: "bold",
  borderRadius: "50%",
  width: "35px",
  height: "35px",
  minWidth: "35px",
  padding: 0,
  "&:hover": {
    background: "#ffcc00",
    boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
  },
};

const whiteDialog = {
  "& .MuiPaper-root": {
    bgcolor: "#ffffff",
    border: "2px solid #FFD600",
    color: "#000000",
    minWidth: "400px",
    maxWidth: "450px",
    width: "100%",
    borderRadius: "12px",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    margin: 0,
  },
};

const whiteInput = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    color: "#000000",
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    fontSize: "0.85rem",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e0e0e0" },
    "&:hover fieldset": { borderColor: "#FFD600", borderWidth: "1.5px" },
    "&.Mui-focused fieldset": { borderColor: "#FFD600", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": {
    color: "#666666",
    fontSize: "0.8rem",
  },
};

function formatThis(dateValue) {
  if (!dateValue) return "—";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).replace(",", "");
}

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return '#00e676';
    case 'lagging':
      return '#ff9800';
    case 'intermittent':
      return '#ff9800';
    case 'down':
      return '#ff4444';
    case 'server_disconected':
      return '#ff4444';
    default:
      return '#999999';
  }
};

const getStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'Active';
    case 'intermittent':
      return 'Intermittent';
    case 'down':
      return 'Down';
    case 'server_disconected':
      return 'Server Disconnected';
    default:
      return status || 'Unknown';
  }
};

const scrollbarStyles = `
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb {
    background: #FFD600;
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #ffcc00;
  }
`;

const TopBar = ({ title, isMobile, onMenuClick, sidebarWidth = 0 }) => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const refreshButtonRef = useRef(null);
  const previousHazardStateRef = useRef(false);
  const notificationIntervalRef = useRef(null);
  const beepIntervalRef = useRef(null);
  const originalTitleRef = useRef(document.title);
  const audioContextRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openPwdDialog, setOpenPwdDialog] = useState(false);
  const [pwdForm, setPwdForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [samePasswordError, setSamePasswordError] = useState(false);
  const [touched, setTouched] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(20);

  const [shiftType, setShiftType] = useState(null);
  const [serverStatus, setServerStatus] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [systemStatus, setSystemStatus] = useState('Unknown');
  const [systemProblem, setSystemProblem] = useState('Ok');
  const [duration, setDuration] = useState(0);

  const [showHazardNotification, setShowHazardNotification] = useState(true);
  const [isSoundMuted, setIsSoundMuted] = useState(false);

  // Check if logged-in user is admin
  const currentUsername = user?.username?.toLowerCase();
  const isAdmin = ADMIN_USERNAMES.includes(currentUsername);
  const REFRESH_INTERVAL = 20000;

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar({ open: false, message: '', severity: 'success' }), 3000);
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // 🔥 Play single beep sound
  const playBeep = useCallback(() => {
    // Don't play if muted or not admin
    if (isSoundMuted || !isAdmin) {
      return;
    }

    try {
      // Close previous audio context if exists
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 400;
      oscillator.type = 'sawtooth';

      gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.8);

      setTimeout(() => {
        try {
          if (audioContext.state === 'closed' || isSoundMuted) return;

          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.frequency.value = 500;
          osc2.type = 'sawtooth';
          gain2.gain.setValueAtTime(0.4, audioContext.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
          osc2.start(audioContext.currentTime);
          osc2.stop(audioContext.currentTime + 0.6);
        } catch (e) {
          console.log("Second beep error:", e);
        }
      }, 300);

    } catch (e) {
      console.log("Beep error:", e);
    }
  }, [isSoundMuted, isAdmin]);

  // 🔥 Start continuous beep sound
  const startContinuousBeep = useCallback(() => {
    // Clear any existing interval
    if (beepIntervalRef.current) {
      clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
    }

    // Don't start if muted or not admin
    if (isSoundMuted || !isAdmin) {
      return;
    }

    // Play first beep immediately
    playBeep();

    // Set interval for subsequent beeps
    beepIntervalRef.current = setInterval(() => {
      if (previousHazardStateRef.current && !isSoundMuted && isAdmin) {
        playBeep();
      } else {
        // Stop if hazard is cleared or muted
        if (beepIntervalRef.current) {
          clearInterval(beepIntervalRef.current);
          beepIntervalRef.current = null;
        }
      }
    }, 5000);

  }, [isSoundMuted, isAdmin, playBeep]);

  // 🔥 Toggle sound mute
  const toggleSoundMute = useCallback(() => {
    setIsSoundMuted((prev) => {
      const newState = !prev;

      // If unmuting and there's a hazard, restart beeps
      if (newState === false && previousHazardStateRef.current && isAdmin) {
        setTimeout(() => startContinuousBeep(), 100);
      }

      // If muting, stop all beeps
      if (newState === true) {
        // Clear interval
        if (beepIntervalRef.current) {
          clearInterval(beepIntervalRef.current);
          beepIntervalRef.current = null;
        }
        // Close audio context
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
      }

      return newState;
    });
  }, [isAdmin, startContinuousBeep]);

  // 🔥 Stop continuous beep
  const stopContinuousBeep = useCallback(() => {
    if (beepIntervalRef.current) {
      clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
  }, []);

  // 🔥 BLINK TAB TITLE
  const startTabAlert = useCallback((message = "⚠️ HAZARD DETECTED!") => {
    const originalTitle = originalTitleRef.current;
    let isAlert = false;

    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
    }

    notificationIntervalRef.current = setInterval(() => {
      document.title = isAlert ? message : originalTitle;
      isAlert = !isAlert;
    }, 500);
  }, []);

  // 🔥 Stop tab alert
  const stopTabAlert = useCallback(() => {
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
      notificationIntervalRef.current = null;
    }
    document.title = originalTitleRef.current;
  }, []);

  // 🔥 Trigger alerts
  const triggerHazardAlerts = useCallback(() => {
    startTabAlert("⚠️ HAZARD DETECTED!");
    if (isAdmin && !isSoundMuted) {
      startContinuousBeep();
    }
  }, [startTabAlert, startContinuousBeep, isAdmin, isSoundMuted]);

  const fetchLastUpdate = useCallback(async (showFeedback = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found');
        return;
      }

      if (showFeedback) {
        setIsRefreshing(true);
      }

      const res = await axios.get(`${BASE_URL}/lastUpdate`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newSystemStatus = res.data.systemStatus || "Unknown";
      const newSystemProblem = res.data.systemProblem || "Ok";
      const newDuration = res.data.duration || 0;

      setLastUpdate(res.data.lastUpdate || "—");
      setShiftType(res.data.shiftType || "Normal");
      setServerStatus(res.data.serverStatus || "Unknown");
      setSystemStatus(newSystemStatus);
      setSystemProblem(newSystemProblem);
      setDuration(newDuration);

      const isHazard = newSystemStatus &&
        newSystemStatus.toLowerCase() !== "active" &&
        newSystemStatus.toLowerCase() !== "unknown" &&
        newDuration >= 0.8;

      const wasHazard = previousHazardStateRef.current;

      if (isHazard && !wasHazard) {
        triggerHazardAlerts();
      }

      if (!isHazard && wasHazard) {
        stopTabAlert();
        stopContinuousBeep();
      }

      previousHazardStateRef.current = isHazard;

      if (showFeedback) {
        showSnackbar('Data refreshed successfully', 'success');
      }

    } catch (err) {
      console.error('Error fetching last update:', err);
      if (showFeedback) {
        showSnackbar("Registry Sync Failed", "error");
      }
    } finally {
      if (showFeedback) {
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  }, [triggerHazardAlerts, stopTabAlert, stopContinuousBeep, showSnackbar]);

  const handleManualRefresh = useCallback(() => {
    fetchLastUpdate(true);
    setCountdown(20);
  }, [fetchLastUpdate]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login', { replace: true });
    handleMenuClose();
  }, [navigate, setUser]);

  // Validate password in real-time
  useEffect(() => {
    if (touched.newPassword && pwdForm.newPassword) {
      const validation = validateComplexPassword(pwdForm.newPassword);
      if (!validation.valid) {
        const errors = validation.msg.split(", ");
        setValidationErrors(errors);
      } else {
        setValidationErrors([]);
      }
    } else if (touched.newPassword && !pwdForm.newPassword) {
      setValidationErrors(["Password is required"]);
    } else {
      setValidationErrors([]);
    }
  }, [pwdForm.newPassword, touched.newPassword]);

  // Check if new password is same as old password
  useEffect(() => {
    if (pwdForm.newPassword && pwdForm.oldPassword && pwdForm.newPassword === pwdForm.oldPassword) {
      setSamePasswordError(true);
    } else {
      setSamePasswordError(false);
    }
  }, [pwdForm.newPassword, pwdForm.oldPassword]);

  // Check if update button should be disabled
  const isUpdateDisabled = useCallback(() => {
    if (loading) return true;
    if (!pwdForm.oldPassword || !pwdForm.newPassword || !pwdForm.confirmPassword) return true;
    if (pwdForm.newPassword !== pwdForm.confirmPassword) return true;
    if (validationErrors.length > 0) return true;
    if (samePasswordError) return true;
    return false;
  }, [loading, pwdForm.oldPassword, pwdForm.newPassword, pwdForm.confirmPassword, validationErrors.length, samePasswordError]);

  const handleChangePassword = useCallback(async () => {
    const { oldPassword, newPassword, confirmPassword } = pwdForm;

    if (!oldPassword || !newPassword || !confirmPassword) {
      showSnackbar('All security fields are required', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showSnackbar('Key mismatch: Verification failed', 'error');
      return;
    }

    const validation = validateComplexPassword(newPassword);
    if (!validation.valid) {
      showSnackbar(`Password must contain: ${validation.msg}`, 'error');
      return;
    }

    if (oldPassword === newPassword) {
      showSnackbar('New password cannot be the same as old password', 'error');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `${BASE_URL}/change-password`,
        { oldPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSnackbar(res.data.msg || 'Security Key Updated', 'success');
      setOpenPwdDialog(false);
      setPwdForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setValidationErrors([]);
      setSamePasswordError(false);
      setTouched({ newPassword: false, confirmPassword: false });
    } catch (err) {
      showSnackbar(err.response?.data?.msg || 'Protocol Failure: Change Rejected', 'error');
    } finally {
      setLoading(false);
    }
  }, [pwdForm, showSnackbar]);

  // Auto-refresh ONLY for admin users
  useEffect(() => {
    if (isAdmin) {
      fetchLastUpdate(false);

      const intervalId = setInterval(() => {
        if (refreshButtonRef.current) {
          refreshButtonRef.current.click();
        }
      }, REFRESH_INTERVAL);

      return () => {
        clearInterval(intervalId);
      };
    }
    fetchLastUpdate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Countdown timer - ONLY for admin users
  useEffect(() => {
    if (isAdmin) {
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 20;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [isAdmin]);

  // 🔥 Store original title on mount and cleanup
  useEffect(() => {
    originalTitleRef.current = document.title;

    return () => {
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current);
        notificationIntervalRef.current = null;
      }
      if (beepIntervalRef.current) {
        clearInterval(beepIntervalRef.current);
        beepIntervalRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      document.title = originalTitleRef.current;
    };
  }, []);

  const hasSystemProblem = systemStatus &&
    systemStatus.toLowerCase() !== "active" &&
    systemStatus.toLowerCase() !== "unknown" &&
    duration >= 0.8;

  const showOverlay = serverStatus === "Down" || serverStatus === "Unknown" || shiftType === "Sunday" || shiftType === "Night";

  const bannerHeight = 0;

  const passwordStrength = getPasswordStrength(pwdForm.newPassword);
  const strengthColor = getStrengthColor(passwordStrength);
  const strengthText = getStrengthText(passwordStrength);

  return (
    <>
      <style>{scrollbarStyles}</style>

      <style>
        {`
          @keyframes pulseRed {
            0% { transform: scale(1); }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); }
          }

          @keyframes shake {
            0% { transform: translateX(0); }
            25% { transform: translateX(-3px); }
            75% { transform: translateX(3px); }
            100% { transform: translateX(0); }
          }

          @keyframes blinkRed {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }

          @keyframes blinkOrange {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          @keyframes glowPulse {
            0% { box-shadow: 0 0 20px rgba(255, 0, 0, 0.6), 0 0 60px rgba(255, 0, 0, 0.3); }
            50% { box-shadow: 0 0 40px rgba(255, 0, 0, 0.9), 0 0 80px rgba(255, 0, 0, 0.5); }
            100% { box-shadow: 0 0 20px rgba(255, 0, 0, 0.6), 0 0 60px rgba(255, 0, 0, 0.3); }
          }
        `}
      </style>

      {/* 🔥 HAZARD NOTIFICATION - BOTTOM RIGHT with RED color */}
      {showHazardNotification && hasSystemProblem && (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 9999,
            animation: "slideIn 0.5s ease-out",
            maxWidth: { xs: "90vw", sm: "420px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              bgcolor: "#d32f2f",
              color: "#ffffff",
              padding: { xs: "12px 16px", sm: "16px 20px" },
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(211, 47, 47, 0.6)",
              border: "2px solid #ff1744",
              animation: "glowPulse 1.5s infinite",
              backdropFilter: "blur(4px)",
              background: "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, flex: 1, minWidth: 0 }}>
              <ErrorIcon sx={{ fontSize: { xs: 20, sm: 28 }, color: "#ffd700", animation: "shake 0.8s infinite", flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "0.7rem", sm: "0.9rem" },
                    letterSpacing: "0.5px",
                  }}
                >
                  ⚠️ HAZARD DETECTED
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.55rem", sm: "0.75rem" },
                    opacity: 0.9,
                    fontWeight: 500,
                  }}
                >
                  {systemStatus} - {systemProblem} - {duration} mins
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
              {/* ✅ MUTE/UNMUTE BUTTON inside the notification */}
              {isAdmin && (
                <Tooltip title={isSoundMuted ? "🔊 Unmute Alarm" : "🔇 Mute Alarm"}>
                  <IconButton
                    onClick={toggleSoundMute}
                    size="small"
                    sx={{
                      bgcolor: isSoundMuted ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.4)",
                      color: "#ffffff",
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 },
                      border: `2px solid ${isSoundMuted ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.5)"}`,
                      "&:hover": {
                        bgcolor: isSoundMuted ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.6)",
                        transform: "scale(1.15)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isSoundMuted ? (
                      <VolumeOffIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    ) : (
                      <VolumeUpIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Hide notification">
                <IconButton
                  onClick={() => {
                    setShowHazardNotification(false);
                    // Also stop beeps when hiding
                    stopContinuousBeep();
                  }}
                  size="small"
                  sx={{
                    bgcolor: "rgba(0,0,0,0.3)",
                    color: "#ffffff",
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    border: "1px solid rgba(255,255,255,0.3)",
                    "&:hover": {
                      bgcolor: "rgba(0,0,0,0.5)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <CloseIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}

      {/* ✅ Shift Type / Server Status Overlay - Centered */}
      {showOverlay && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            px: 3,
            py: 2,
            borderRadius: "8px",
            bgcolor: "rgba(0,0,0,0.85)",
            border: serverStatus === "Down" ? "3px solid #d32f2f" : shiftType === "Night" ? "3px solid #ffffff" : "3px solid #e65100",
            animation: serverStatus === "Down" ? "blinkRed 1s infinite" : "blinkOrange 1.2s infinite",
            width: "auto",
            minWidth: "250px",
            maxWidth: "90vw",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: serverStatus === "Down" ? "#ff4444" : shiftType === "Night" ? "#ffffff" : "#ffaa00",
              fontWeight: "bold",
              fontSize: { xs: "1rem", sm: "1.2rem" },
              textAlign: "center",
              letterSpacing: "1px",
            }}
          >
            {serverStatus === "Down"
              ? "🚩 SERVER DISCONNECTED"
              : serverStatus === "Unknown"
                ? "⚠️ SERVER IP NOT SET"
                : shiftType === "Sunday"
                  ? "🟡 SUNDAY MODE"
                  : shiftType === "Night"
                    ? "🌙 NIGHT MODE"
                    : ""}
          </Typography>
        </Box>
      )}

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#ffffff',
          borderBottom: `1px solid ${theme.palette.primary.main}`,
          color: '#000000',
          width: '100%',
          mt: `${bannerHeight}px`,
        }}
      >
        <Toolbar
          sx={{
            minHeight: 60,
            height: 60,
            px: { xs: 1.5, sm: 3 },
            position: 'relative',
          }}
        >
          {/* Menu Icon - Only on mobile */}
          {isMobile && (
            <IconButton
              edge="start"
              onClick={onMenuClick}
              sx={{
                color: theme.palette.primary.main,
                mr: 1,
                width: 40,
                height: 40,
                p: 0,
              }}
            >
              <MenuIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}

          {/* Title - Only on desktop */}
          {!isMobile && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#000000',
                lineHeight: 1.2,
                ml: `${sidebarWidth}px`,
                transition: 'margin-left 0.25s ease-in-out',
                flexGrow: 1,
              }}
            >
              {title}
            </Typography>
          )}

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 2 },
            flex: isMobile ? 1 : 'none',
            justifyContent: isMobile ? 'flex-end' : 'flex-start',
            ml: isMobile ? 0 : 'auto',
            width: isMobile ? '100%' : 'auto',
          }}>
            {/* Mobile elements */}
            {isMobile && (
              <>
                <Tooltip title="Last synchronization time">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      border: '1px solid #e0e0e0',
                      px: 1,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: '#f5f5f5',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#fbc02d", fontWeight: 700, fontSize: '0.7rem' }}>
                      @
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#000000', fontWeight: 700, fontSize: '0.7rem' }}>
                      {formatThis(lastUpdate)}
                    </Typography>
                  </Box>
                </Tooltip>

                <Tooltip title={`System Status: ${getStatusLabel(systemStatus)}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 'bold',
                        color: '#000000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Sym:
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        border: '1px solid #e0e0e0',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        bgcolor: '#f5f5f5',
                      }}
                    >
                      <CircleIcon
                        sx={{
                          color: getStatusColor(systemStatus),
                          fontSize: 16,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 'bold',
                          color: '#000000',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getStatusLabel(systemStatus)}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>

                <Tooltip title="Refresh data manually">
                  <IconButton
                    ref={refreshButtonRef}
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                    size="small"
                    sx={{
                      ...yellowButton,
                      background: "transparent",
                      border: "1px solid #FFD600",
                      flexShrink: 0,
                      width: '36px',
                      height: '36px',
                      minWidth: '36px',
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                </Tooltip>

                <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
                  <Avatar
                    sx={{
                      bgcolor: '#FFD600',
                      color: '#000000',
                      width: 40,
                      height: 40,
                    }}
                  >
                    <AccountCircleIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                </IconButton>
              </>
            )}

            {/* Desktop elements */}
            {!isMobile && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: isAdmin ? '#f3ffe1' : '#f5f5f5',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    border: `1px solid ${isAdmin ? '#FFD600' : '#e0e0e0'}`,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#000000', fontWeight: 500 }}>
                    @:  {formatThis(lastUpdate)}
                  </Typography>
                </Box>

                <Tooltip title="Refresh data manually">
                  <IconButton
                    ref={refreshButtonRef}
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                    sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600", flexShrink: 0 }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>

                {isAdmin && (
                  <Tooltip title={`Auto-refreshes in ${countdown} seconds (Admin Mode: 20s interval)`}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: '#FFF8E1',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        border: '1px solid #FFD600',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#000000', fontWeight: 500 }}>
                        Refresh In: {countdown}s
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#000000', fontWeight: 'bold' }}>
                        (LIVE)
                      </Typography>
                    </Box>
                  </Tooltip>
                )}

                <Tooltip title={`System Status: ${getStatusLabel(systemStatus)}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 'bold',
                        color: '#000000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      System:
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        border: '1px solid #e0e0e0',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        bgcolor: '#f5f5f5',
                      }}
                    >
                      <CircleIcon
                        sx={{
                          color: getStatusColor(systemStatus),
                          fontSize: 16,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 'bold',
                          color: '#000000',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getStatusLabel(systemStatus)}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>

                <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
                  <Avatar
                    sx={{
                      bgcolor: '#FFD600',
                      color: '#000000',
                      width: 40,
                      height: 40,
                    }}
                  >
                    <AccountCircleIcon />
                  </Avatar>
                </IconButton>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#000000',
                    fontWeight: 500,
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  {user?.username ? `(${user.username})` : '(User)'}
                </Typography>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            bgcolor: '#ffffff',
            border: `1px solid #FFD600`,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }
        }}
      >
        <Box sx={{ px: 2, py: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Avatar sx={{ bgcolor: '#FFD600', color: '#000000', width: 50, height: 50 }}>
              <AccountCircleIcon sx={{ fontSize: 30 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000000' }}>
                {user?.username ? `(${user.username})` : '(User)'}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 1, borderColor: '#e0e0e0' }} />
          <Box display="flex" flexDirection="column" gap={0.8}>
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon sx={{ color: '#FFD600', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#666666' }}>
                Name: <span style={{ color: '#000000', fontWeight: 900 }}>{user?.name || 'N/A'}</span>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <BadgeIcon sx={{ color: '#FFD600', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#666666' }}>
                Username: <span style={{ color: '#000000', fontWeight: 900 }}>{user?.username || 'N/A'}</span>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon sx={{ color: '#FFD600', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#666666' }}>
                Company: <span style={{ color: '#000000', fontWeight: 900 }}>{user?.company || 'N/A'}</span>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon sx={{ color: '#FFD600', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#666666' }}>
                Role: <span style={{ color: '#000000', fontWeight: 900 }}>{user?.role || 'User'}</span>
              </Typography>
            </Box>
          </Box>
        </Box>

        <MenuItem
          onClick={() => { handleMenuClose(); setOpenPwdDialog(true); }}
          sx={{ py: 1.5, '&:hover': { bgcolor: 'rgba(255, 214, 0, 0.1)' } }}
        >
          <ListItemIcon>
            <LockResetIcon sx={{ color: '#FFD600' }} />
          </ListItemIcon>
          <ListItemText
            primary="Change Password"
            primaryTypographyProps={{ sx: { color: '#000000', fontWeight: 500 } }}
          />
        </MenuItem>

        <Divider sx={{ borderColor: '#e0e0e0' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ py: 1.5, '&:hover': { bgcolor: 'rgba(255, 68, 68, 0.1)' } }}
        >
          <ListItemIcon>
            <PowerSettingsNewIcon sx={{ color: '#ff4444' }} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ sx: { color: '#ff4444', fontWeight: 500 } }}
          />
        </MenuItem>
      </Menu>

      {/* Enhanced Password Dialog */}
      <Dialog
        open={openPwdDialog}
        onClose={() => {
          setOpenPwdDialog(false);
          setPwdForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
          setValidationErrors([]);
          setSamePasswordError(false);
          setTouched({ newPassword: false, confirmPassword: false });
        }}
        sx={whiteDialog}
        PaperProps={{
          sx: {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            m: 0,
            width: '100%',
            maxWidth: '450px',
          }
        }}
      >
        <DialogTitle sx={{ color: "#FFD600", bgcolor: "#ffffff", fontWeight: "bold", borderBottom: "1px solid #e0e0e0", fontSize: "1.1rem" }}>
          CHANGE YOUR PASSWORD
        </DialogTitle>
        <DialogContent sx={{ mt: 2, bgcolor: "#ffffff" }}>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Old Password"
              type={showPasswords.old ? "text" : "password"}
              fullWidth
              size="small"
              sx={whiteInput}
              value={pwdForm.oldPassword}
              onChange={(e) => setPwdForm({ ...pwdForm, oldPassword: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPasswords(prev => ({ ...prev, old: !prev.old }))}
                      edge="end"
                    >
                      {showPasswords.old ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="New Password"
              type={showPasswords.new ? "text" : "password"}
              fullWidth
              size="small"
              sx={whiteInput}
              value={pwdForm.newPassword}
              onChange={(e) => {
                setPwdForm({ ...pwdForm, newPassword: e.target.value });
                const validation = validateComplexPassword(e.target.value);
                setValidationErrors(validation.valid ? [] : validation.msg.split(", "));
              }}
              onBlur={() => setTouched(prev => ({ ...prev, newPassword: true }))}
              error={(touched.newPassword && pwdForm.newPassword !== "" && validationErrors.length > 0) || samePasswordError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      edge="end"
                    >
                      {showPasswords.new ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {pwdForm.newPassword && !samePasswordError && (
              <Box sx={{ mt: -1, mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ flex: 1, height: 4, bgcolor: "#e0e0e0", borderRadius: 2, overflow: "hidden" }}>
                    <Box
                      sx={{
                        width: `${(passwordStrength / 5) * 100}%`,
                        height: "100%",
                        bgcolor: strengthColor,
                        transition: "width 0.3s ease"
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: "0.65rem", color: strengthColor, fontWeight: 600 }}>
                    {strengthText}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#666", display: "block", mt: 0.5 }}>
                  Password must contain: 8+ chars, uppercase, lowercase, number, special character
                </Typography>
              </Box>
            )}

            {samePasswordError && (
              <FormHelperText error sx={{ mt: -1, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                <CancelIcon sx={{ fontSize: 14 }} />
                New password cannot be the same as old password
              </FormHelperText>
            )}

            {!samePasswordError && touched.newPassword && pwdForm.newPassword !== "" && validationErrors.length > 0 && (
              <FormHelperText error sx={{ mt: -1, mb: 1, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                <CancelIcon sx={{ fontSize: 14 }} />
                {validationErrors.map((err, idx) => (
                  <span key={idx}>
                    {idx > 0 ? " • " : ""}
                    {err}
                  </span>
                ))}
              </FormHelperText>
            )}

            {!samePasswordError && touched.newPassword && pwdForm.newPassword !== "" && validationErrors.length === 0 && (
              <FormHelperText sx={{ mt: -1, mb: 1, color: "#4caf50", display: "flex", alignItems: "center", gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 14 }} />
                Strong password!
              </FormHelperText>
            )}

            <TextField
              label="Verify Password"
              type={showPasswords.confirm ? "text" : "password"}
              fullWidth
              size="small"
              sx={whiteInput}
              value={pwdForm.confirmPassword}
              onChange={(e) => setPwdForm({ ...pwdForm, confirmPassword: e.target.value })}
              onBlur={() => setTouched(prev => ({ ...prev, confirmPassword: true }))}
              error={touched.confirmPassword && pwdForm.confirmPassword !== "" && pwdForm.newPassword !== pwdForm.confirmPassword}
              helperText={touched.confirmPassword && pwdForm.confirmPassword !== "" && pwdForm.newPassword !== pwdForm.confirmPassword ? "Passwords do not match" : " "}
              FormHelperTextProps={{ sx: { fontSize: "0.65rem" } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      edge="end"
                    >
                      {showPasswords.confirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, bgcolor: "#ffffff", borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={() => {
              setOpenPwdDialog(false);
              setPwdForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
              setValidationErrors([]);
              setSamePasswordError(false);
              setTouched({ newPassword: false, confirmPassword: false });
            }}
            sx={{ color: "#666666", textTransform: "none", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={isUpdateDisabled()}
            sx={{
              bgcolor: "#FFD600",
              color: "#000000",
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": { bgcolor: "#ffcc00" },
              "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#999" }
            }}
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 9999,
            bgcolor: '#ffffff',
            color: '#000000',
            border: `1px solid ${snackbar.severity === 'success' ? '#FFD600' : '#ff4444'}`,
          }}
        >
          {snackbar.message}
        </Alert>
      )}
    </>
  );
};

export default TopBar;